import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CompanyFilesContext from '../../contexts/data/companies/CompanyFilesContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { Table, Button, Space, Popconfirm, message, Flex, Typography, Pagination, Tooltip } from 'antd';
import { DownloadOutlined, ReloadOutlined, DeleteOutlined, UploadOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { ICompanyFile } from '@leedpulse-monorepo/interfaces';
import deleteCompanyFiles from '../../../domain/use_cases/companies/deleteCompanyFiles';
import downloadCompanyFile from '../../../domain/use_cases/companies/downloadCompanyFile';
import downloadCompanyFiles from '../../../domain/use_cases/companies/downloadCompanyFiles';
import CompanyFileUploadModal from './company_files/CompanyFileUploadModal';
import CompanyFileDetailsModal from './company_files/CompanyFileDetailsModal';

const DESCRIPTION_MAX_LENGTH = 50;

interface CompanyFilesListProps {
    companyId: string;
}

export default function CompanyFilesList({ companyId }: CompanyFilesListProps) {
    const companyFilesContext = useContext(CompanyFilesContext);
    const authContext = useContext(AuthenticationContext);

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [deleting, setDeleting] = useState(false);
    const [downloadingZip, setDownloadingZip] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [selectedCompanyFile, setSelectedCompanyFile] = React.useState<ICompanyFile | undefined>(undefined);
    const [searchParams, setSearchParams] = useSearchParams();
    const openedFileRef = useRef<string | null>(null);

    const handlePaginationChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        companyFilesContext.fetch(companyId, authContext.authentication, page, pageSize);
    };

    const handleRefresh = () => {
        setCurrentPage(1);
        setSelectedRowKeys([]);
        companyFilesContext.fetch(companyId, authContext.authentication);
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteCompanyFiles(companyId, selectedRowKeys as string[], authContext.authentication);
            message.success('Fichiers supprimés avec succès');
            setSelectedRowKeys([]);
            setCurrentPage(1);
            companyFilesContext.fetch(companyId, authContext.authentication);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors de la suppression');
        } finally {
            setDeleting(false);
        }
    };

    const handleUpdateCallback = () => {
        setCurrentPage(1);
        companyFilesContext.fetch(companyId, authContext.authentication);
        setSelectedCompanyFile(undefined);
        setIsUploadModalOpen(false);
    };

    const handleDownloadZip = async () => {
        setDownloadingZip(true);
        try {
            const { url, filename } = await downloadCompanyFiles(companyId, selectedRowKeys as string[], authContext.authentication);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors du téléchargement');
        } finally {
            setDownloadingZip(false);
        }
    };

    const handleDownload = async (record: ICompanyFile) => {
        try {
            const { url, filename } = await downloadCompanyFile(companyId, record, authContext.authentication);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors du téléchargement');
        }
    };

    const handleDeleteFile = async (fileId: string) => {
        try {
            await deleteCompanyFiles(companyId, [fileId], authContext.authentication);
            message.success('Fichier supprimé avec succès');
            setSelectedRowKeys((keys) => keys.filter((k) => k !== fileId));
            companyFilesContext.fetch(companyId, authContext.authentication, currentPage);
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors de la suppression');
        }
    };

    const handleCancelDetailsModal = () => {
        setSelectedCompanyFile(undefined);
        searchParams.delete('file');
        setSearchParams(searchParams, { replace: true });
    };

    useEffect(() => {
        if (
            companyFilesContext.status === ContextStatus.IDLE ||
            companyFilesContext.scopedCompanyId !== companyId
        ) {
            companyFilesContext.fetch(companyId, authContext.authentication);
        }
        return () => {
            companyFilesContext.reset();
        };
    }, [companyId]);

    useEffect(() => {
        const fileId = searchParams.get('file');
        if (!fileId || openedFileRef.current === fileId) return;
        const file = companyFilesContext.items.find((f) => f.id === fileId);
        if (file) {
            openedFileRef.current = fileId;
            // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
            // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
            // external system") ; le setState synchrone est le reset qui precede l'appel.
            // eslint-disable-next-line react/set-state-in-effect
            setSelectedCompanyFile(file);
        }
    }, [searchParams, companyFilesContext.items]);

    const columns = [
        {
            title: 'Type',
            dataIndex: 'mime_type',
            key: 'mime_type',
            render: (mime_type: ICompanyFile['mime_type']) => mime_type,
        },
        {
            title: 'Nom',
            dataIndex: 'name',
            key: 'name',
            render: (_: unknown, record: ICompanyFile) => (
                <Space size="small">
                    {record.sensitive && (
                        <Tooltip title="Fichier sensible">
                            <EyeInvisibleOutlined style={{ color: '#cf1322' }} />
                        </Tooltip>
                    )}
                    {record.name}
                </Space>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (description: ICompanyFile['description']) => <Typography.Text ellipsis>{description}</Typography.Text>,
        },
        {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (timestamp: number) => moment(timestamp).format('DD/MM/YYYY à HH:mm'),
        },
        {
            title: '',
            key: 'actions',
            render: (record: ICompanyFile) =>
                <Flex gap="small" justify="end">
                    <Button type="primary" onClick={() => handleDownload(record)} icon={<DownloadOutlined />} />

                    <Popconfirm
                        title="Supprimer le fichier"
                        description="Cette action est irréversible. Confirmer la suppression ?"
                        onConfirm={() => handleDeleteFile(record.id)}
                        okText="Supprimer"
                        cancelText="Annuler"
                    >
                        <Button danger type="primary" icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Flex>
        },
    ];

    return (
        <div className="list-container">
            <div className="list-container__controls">
                <Flex gap="small" align='baseline'>
                    <Typography.Title level={5}>Fichiers ({companyFilesContext.count})</Typography.Title>
                </Flex>
                <Space>
                    <Button onClick={() => setIsUploadModalOpen(true)} icon={<UploadOutlined />}>
                        Téléverser des fichiers
                    </Button>
                    <Button type="primary" onClick={handleRefresh} icon={<ReloadOutlined />}>
                        Actualiser
                    </Button>
                </Space>
            </div>

            <Flex justify="space-between" align="baseline" style={{ marginBottom: "1rem" }}>
                <Flex vertical gap="small">
                    {selectedRowKeys.length > 0 ? <>
                        <Flex align="baseline" gap="small">
                            <Button
                                icon={<DownloadOutlined />}
                                loading={downloadingZip}
                                onClick={handleDownloadZip}
                            >
                                Télécharger
                            </Button>
                            <Popconfirm
                                title="Supprimer les fichiers sélectionnés"
                                description="Cette action est irréversible. Confirmer la suppression ?"
                                onConfirm={handleDelete}
                                okText="Supprimer"
                                cancelText="Annuler"
                                disabled={selectedRowKeys.length === 0}
                            >
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deleting}
                                    disabled={selectedRowKeys.length === 0}
                                >
                                    Supprimer
                                </Button>
                            </Popconfirm>
                        </Flex>
                        <h4 style={{ marginLeft: '0.5rem', marginBottom: '0rem' }}>
                            {selectedRowKeys.length} fichier(s) sélectionné(s)
                        </h4>
                    </> : <div />}
                </Flex>

                <Pagination
                    current={currentPage}
                    total={companyFilesContext.count}
                    pageSize={companyFilesContext.hitsPerPage}
                    onChange={handlePaginationChange}
                    showSizeChanger={true}
                    pageSizeOptions={[20, 50, 100, 200]}
                    disabled={companyFilesContext.status === ContextStatus.LOADING}
                />
            </Flex>

            <Table
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                }}
                onRow={(record) => ({
                    onClick: (e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('button') || target.closest('.ant-table-selection-column') || target.closest('.ant-checkbox-wrapper')) return;
                        setSelectedCompanyFile(record);
                    },
                    style: { cursor: 'pointer' },
                })}
                pagination={{
                    current: currentPage,
                    total: companyFilesContext.count,
                    pageSize: companyFilesContext.hitsPerPage,
                    onChange: handlePaginationChange,
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100, 200],
                    position: ['bottomRight'],
                    disabled: companyFilesContext.status === ContextStatus.LOADING,
                }}
                loading={companyFilesContext.status === ContextStatus.LOADING}
                columns={columns}
                rowKey={(record) => record.id}
                dataSource={companyFilesContext.items}
            />

            <CompanyFileUploadModal
                open={isUploadModalOpen}
                onCancel={() => setIsUploadModalOpen(false)}
                companyId={companyId}
                onUpdateCallback={handleUpdateCallback}
                maxDescriptionLength={DESCRIPTION_MAX_LENGTH}
            />

            <CompanyFileDetailsModal
                companyFile={selectedCompanyFile}
                onCancel={handleCancelDetailsModal}
                companyId={companyId}
                onUpdateCallback={handleUpdateCallback}
                maxDescriptionLength={DESCRIPTION_MAX_LENGTH}
                downloadingZip={downloadingZip}
                onDownloadZip={handleDownload}
            />
        </div>
    );
}
